import { useMutation, useQueryClient, type UseMutateFunction } from '@tanstack/react-query';
import { verificationService } from '../services/verification.service';
import type { CreateVerificationRequest, Verification } from '../types/verification.types';

interface CreateVerificationData {
  candidateId: string;
  experienceId: string;
  request: CreateVerificationRequest;
}

export function useCreateVerification(): {
  createVerification: UseMutateFunction<Verification, Error, CreateVerificationData>;
  createVerificationAsync: (data: CreateVerificationData) => Promise<unknown>;
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
  error: Error | null;
  reset: () => void;
} {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (data: CreateVerificationData) =>
      verificationService.createVerification(data.candidateId, data.experienceId, data.request),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          'verifications',
          { candidateId: variables.candidateId, experienceId: variables.experienceId },
        ],
      });
    },
  });

  return {
    createVerification: mutation.mutate,
    createVerificationAsync: mutation.mutateAsync,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    isSuccess: mutation.isSuccess,
    error: mutation.error,
    reset: mutation.reset,
  };
}
